import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

/**
 * FilterBreadcrumb Component
 * 
 * Displays active filters as breadcrumb pills that can be removed individually
 */
const FilterBreadcrumb = ({ filters, onRemoveFilter, onClearAll, transactions = [], categories = [] }) => {
  // Extract unique accounts for display
  const getAccountNames = (accountIds) => {
    if (!accountIds || accountIds.length === 0) return [];
    
    const accountMap = new Map();
    transactions.forEach(tx => {
      let accountId = null;
      let accountName = null;
      
      if (tx.wallet) {
        if (typeof tx.wallet === 'object' && tx.wallet._id) {
          accountId = tx.wallet._id.toString();
          const lastFour = tx.wallet.lastFourDigits || tx.wallet.mask || '';
          accountName = lastFour ? `${tx.wallet.name || tx.wallet.institution || 'Account'} ••${lastFour}` : (tx.wallet.name || 'Account');
        } else if (typeof tx.wallet === 'string') {
          accountId = tx.wallet;
        }
      }
      
      if (!accountId && tx.account) {
        if (typeof tx.account === 'object' && tx.account._id) {
          accountId = tx.account._id.toString();
          accountName = tx.account.name || 'Account';
        } else if (typeof tx.account === 'string') {
          accountId = tx.account;
        }
      }
      
      if (accountId && accountIds.includes(accountId) && !accountMap.has(accountId)) {
        accountMap.set(accountId, accountName || `Account ${accountId.substring(0, 8)}`);
      }
    });
    
    return Array.from(accountMap.values());
  };

  const activeFilters = [];

  // Type filter
  if (filters.type && filters.type !== 'all') {
    activeFilters.push({
      key: 'type',
      label: `Type: ${filters.type === 'expense' ? 'Expenses' : 'Income'}`,
      value: filters.type
    });
  }

  // Category filter - support both single category and multiple categories
  if (filters.category && (
    (Array.isArray(filters.category) && filters.category.length > 0) ||
    (!Array.isArray(filters.category) && filters.category !== '' && filters.category !== 'all')
  )) {
    // Normalize to array for consistent handling
    const categoryIds = Array.isArray(filters.category) 
      ? filters.category 
      : [filters.category];
    
    // Look up category names from categories array
    const categoryNames = categoryIds.map(categoryId => {
      const idStr = String(categoryId).trim();
      const category = categories.find(cat => {
        const catId = String(cat._id || cat.id || cat).trim();
        return catId === idStr;
      });
      return category?.name || 'Unknown Category';
    });
    
    // Display label based on number of categories
    let label;
    if (categoryNames.length === 1) {
      label = `Category: ${categoryNames[0]}`;
    } else {
      label = `Categories: ${categoryNames.join(', ')}`;
    }
    
    activeFilters.push({
      key: 'category',
      label: label,
      value: filters.category
    });
  }

  // Owner filter
  if (filters.owner && filters.owner !== 'all') {
    const ownerLabels = {
      personal: 'Personal',
      partner: 'Partner',
      joint: 'Joint'
    };
    activeFilters.push({
      key: 'owner',
      label: `Owner: ${ownerLabels[filters.owner] || filters.owner}`,
      value: filters.owner
    });
  }

  // Source filter
  if (filters.source && filters.source !== 'all') {
    activeFilters.push({
      key: 'source',
      label: `Source: ${filters.source === 'plaid' ? 'Plaid' : 'Manual'}`,
      value: filters.source
    });
  }

  // Search filter
  if (filters.search && filters.search.trim()) {
    activeFilters.push({
      key: 'search',
      label: `Search: "${filters.search}"`,
      value: filters.search
    });
  }

  // Date range filter
  if (filters.dateFrom || filters.dateTo) {
    const from = filters.dateFrom ? new Date(filters.dateFrom).toLocaleDateString() : '';
    const to = filters.dateTo ? new Date(filters.dateTo).toLocaleDateString() : '';
    activeFilters.push({
      key: 'dateRange',
      label: `Date: ${from} - ${to || 'Today'}`,
      value: { dateFrom: filters.dateFrom, dateTo: filters.dateTo }
    });
  }

  // Amount range filter
  if (filters.amountMin || filters.amountMax) {
    const min = filters.amountMin ? `$${parseFloat(filters.amountMin).toLocaleString()}` : '';
    const max = filters.amountMax ? `$${parseFloat(filters.amountMax).toLocaleString()}` : '';
    activeFilters.push({
      key: 'amountRange',
      label: `Amount: ${min || 'Any'} - ${max || 'Any'}`,
      value: { amountMin: filters.amountMin, amountMax: filters.amountMax }
    });
  }

  // Accounts filter
  if (filters.accounts && Array.isArray(filters.accounts) && filters.accounts.length > 0) {
    const accountNames = getAccountNames(filters.accounts);
    if (accountNames.length > 0) {
      if (accountNames.length === 1) {
        activeFilters.push({
          key: 'accounts',
          label: `Account: ${accountNames[0]}`,
          value: filters.accounts
        });
      } else {
        activeFilters.push({
          key: 'accounts',
          label: `Accounts: ${accountNames.length} selected`,
          value: filters.accounts
        });
      }
    }
  }

  // Merchant filter
  if (filters.merchant && filters.merchant.trim()) {
    activeFilters.push({
      key: 'merchant',
      label: `Merchant: "${filters.merchant}"`,
      value: filters.merchant
    });
  }

  // Shared filter (ownerType)
  if (filters.shared && filters.shared !== 'all') {
    // Map filter values to display labels
    // filters.shared can be: 'personal', 'partner', 'joint'
    let sharedLabel = filters.shared;
    if (filters.shared === 'personal') {
      sharedLabel = 'Personal Only';
    } else if (filters.shared === 'partner') {
      sharedLabel = 'Partner Only';
    } else if (filters.shared === 'joint') {
      sharedLabel = 'Joint Only';
    }
    activeFilters.push({
      key: 'shared',
      label: `Shared: ${sharedLabel}`,
      value: filters.shared
    });
  }

  // Recurring filter
  if (filters.recurring && filters.recurring !== 'all') {
    activeFilters.push({
      key: 'recurring',
      label: `Recurring: ${filters.recurring === 'recurring' ? 'Yes' : 'One-Time'}`,
      value: filters.recurring
    });
  }

  // Notes filter
  if (filters.notes && filters.notes.trim()) {
    activeFilters.push({
      key: 'notes',
      label: `Notes: "${filters.notes}"`,
      value: filters.notes
    });
  }

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 flex-wrap mb-4 p-2 bg-gray-50 rounded-lg">
      <span className="text-xs font-medium text-gray-600">Active Filters:</span>
      {activeFilters.map((filter, index) => (
        <span
          key={index}
          className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium"
        >
          {filter.label}
          <button
            type="button"
            onClick={() => onRemoveFilter(filter.key, filter.value)}
            className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
            title="Remove filter"
          >
            <XMarkIcon className="h-3 w-3" />
          </button>
        </span>
      ))}
      {activeFilters.length > 1 && (
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs text-gray-500 hover:text-gray-700 font-medium ml-2"
        >
          Clear All
        </button>
      )}
    </div>
  );
};

export default FilterBreadcrumb;

